/**
 * Minimal, dependency-free Markdown renderer for chat messages.
 *
 * Builds React elements directly (never innerHTML), so there is no XSS surface.
 * Supports the subset the assistant actually emits: headings, bold, italic,
 * inline code, fenced code blocks, bullet / numbered lists, GitHub-style tables,
 * links, and paragraphs. Styled for the admin panel's dark theme.
 */
import React from "react";

const BORDER = "rgba(255,255,255,0.08)";

const s: Record<string, React.CSSProperties> = {
  root: { display: "flex", flexDirection: "column", gap: 8 },
  p: { margin: 0, lineHeight: 1.55 },
  h: { margin: "2px 0 0", fontWeight: 700, color: "#f4f4f5", lineHeight: 1.35 },
  ul: {
    margin: 0,
    paddingLeft: 20,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  li: { lineHeight: 1.5 },
  code: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12.5,
    background: "rgba(255,255,255,0.09)",
    padding: "1px 5px",
    borderRadius: 4,
    color: "#e4e4e7",
  },
  pre: {
    margin: 0,
    background: "#0f0f14",
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: 10,
    overflowX: "auto",
  },
  preCode: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12.5,
    color: "#e4e4e7",
    background: "none",
    padding: 0,
    whiteSpace: "pre",
  },
  a: { color: "#a5b4fc", textDecoration: "underline" },
  strong: { fontWeight: 700, color: "#f4f4f5" },
  tableWrap: { overflowX: "auto" },
  table: { borderCollapse: "collapse", width: "100%", fontSize: 13 },
  th: {
    border: `1px solid ${BORDER}`,
    padding: "5px 8px",
    textAlign: "left",
    background: "rgba(255,255,255,0.04)",
    fontWeight: 600,
    color: "#e4e4e7",
  },
  td: { border: `1px solid ${BORDER}`, padding: "5px 8px", textAlign: "left" },
};

// Reset to 0 at the start of each render (see MarkdownMessage) so keys are
// deterministic for the same content across renders — stable reconciliation.
let keySeq = 0;
const nextKey = (): string => `md-${keySeq++}`;

// Only these URL schemes are allowed on rendered links; anything else (e.g.
// javascript:, data:) is rendered as plain text so model/log content can't
// inject a clickable script URL.
const SAFE_URL = /^(https?:|mailto:)/i;

/** Inline: **bold**, *italic* / _italic_, `code`, [text](url). Recurses for nesting. */
function renderInline(text: string): React.ReactNode[] {
  const patterns: Array<{
    re: RegExp;
    kind: "code" | "strong" | "em" | "link";
  }> = [
    { re: /`([^`]+)`/, kind: "code" },
    { re: /\*\*([^*]+)\*\*/, kind: "strong" },
    { re: /\*([^*\n]+)\*/, kind: "em" },
    { re: /_([^_\n]+)_/, kind: "em" },
    { re: /\[([^\]]+)\]\(([^)\s]+)\)/, kind: "link" },
  ];

  let earliest: { index: number; m: RegExpExecArray; kind: string } | null =
    null;
  for (const p of patterns) {
    const m = p.re.exec(text);
    if (m && (earliest === null || m.index < earliest.index)) {
      earliest = { index: m.index, m, kind: p.kind };
    }
  }
  if (!earliest) return [text];

  const { m, kind } = earliest;
  const out: React.ReactNode[] = [];
  const before = text.slice(0, m.index);
  if (before) out.push(before);

  if (kind === "code") {
    out.push(
      <code key={nextKey()} style={s.code}>
        {m[1]}
      </code>,
    );
  } else if (kind === "strong") {
    out.push(
      <strong key={nextKey()} style={s.strong}>
        {renderInline(m[1])}
      </strong>,
    );
  } else if (kind === "em") {
    out.push(<em key={nextKey()}>{renderInline(m[1])}</em>);
  } else if (kind === "link") {
    out.push(
      SAFE_URL.test(m[2]) ? (
        <a
          key={nextKey()}
          href={m[2]}
          target="_blank"
          rel="noreferrer"
          style={s.a}
        >
          {renderInline(m[1])}
        </a>
      ) : (
        // Unsafe scheme — render the label as plain text, not a link.
        <span key={nextKey()}>{renderInline(m[1])}</span>
      ),
    );
  }

  out.push(...renderInline(text.slice(m.index + m[0].length)));
  return out;
}

const splitRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());

/** Parse the whole message into block-level React elements. */
function renderBlocks(md: string): React.ReactNode[] {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Fenced code block
    if (line.trim().startsWith("```")) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```"))
        body.push(lines[i++]);
      i++; // closing fence
      blocks.push(
        <pre key={nextKey()} style={s.pre}>
          <code style={s.preCode}>{body.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    // Heading
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      blocks.push(
        <div key={nextKey()} style={{ ...s.h, fontSize: level <= 2 ? 15 : 14 }}>
          {renderInline(heading[2])}
        </div>,
      );
      i++;
      continue;
    }

    // Table (header row + separator row of dashes)
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      lines[i + 1].includes("|") &&
      /^\s*\|?[\s:-]*-{2,}[\s:|-]*$/.test(lines[i + 1])
    ) {
      const header = splitRow(line);
      i += 2; // skip header + separator
      const rows: string[][] = [];
      while (
        i < lines.length &&
        lines[i].includes("|") &&
        lines[i].trim() !== ""
      ) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push(
        <div key={nextKey()} style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                {header.map((h) => (
                  <th key={nextKey()} style={s.th}>
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={nextKey()}>
                  {r.map((c) => (
                    <td key={nextKey()} style={s.td}>
                      {renderInline(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Lists (bullet or numbered) — consecutive matching lines
    const listMatch = /^(\s*)([-*]|\d+\.)\s+(.*)$/.exec(line);
    if (listMatch) {
      const ordered = /\d+\./.test(listMatch[2]);
      const items: React.ReactNode[] = [];
      while (i < lines.length) {
        const im = /^(\s*)([-*]|\d+\.)\s+(.*)$/.exec(lines[i]);
        if (!im) break;
        items.push(
          <li key={nextKey()} style={s.li}>
            {renderInline(im[3])}
          </li>,
        );
        i++;
      }
      blocks.push(
        ordered ? (
          <ol key={nextKey()} style={s.ul}>
            {items}
          </ol>
        ) : (
          <ul key={nextKey()} style={s.ul}>
            {items}
          </ul>
        ),
      );
      continue;
    }

    // Paragraph — gather until a blank line or a new block starts
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].trim().startsWith("```") &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !/^(\s*)([-*]|\d+\.)\s+/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <div key={nextKey()} style={s.p}>
        {renderInline(para.join("\n"))}
      </div>,
    );
  }

  return blocks;
}

export const MarkdownMessage: React.FC<{ content: string }> = ({ content }) => {
  // Reset the key counter so identical content yields identical keys each render.
  keySeq = 0;
  return <div style={s.root}>{renderBlocks(content)}</div>;
};
